import config from '../../config';
import { Api, ApiConfig } from '../api';
import type { AxiosRequestConfig } from 'axios';

// 响应类型定义
export interface BetaStatusResponse {
    betaAccess: boolean;
}

export interface RedeemCodeResponse {
    success: boolean;
    message?: string;
}

export interface ShopBinding {
    id: string;
    shopId: string;
    domain: string;
}

export interface ShopInfo {
    id: string;
    domain: string;
    brandName: string;
    platform: string;
    timezone: string;
    physicFlag: number;
}

export interface ShopifyConnectRequest {
    shop_domain: string;
    redirect_url: string;
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface ShopifyConnectResponse {
    shop: string;
    authorizationUrl: string;
}

export interface ShopifyDisconnectRequest {
    shop_domain: string;
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface ShopifyDisconnectResponse {
    success: boolean;
    message?: string;
}

export interface UserInfo {
    id: string;
    email: string;
    userName: string;
}

export interface Workspace {
    id: string;
    name: string;
    logoUrl: string | null;
    role: string;
}

export interface WorkspacePlan {
    planId: string;
    planName: string;
    planCode: string;
    tier: string;
    status: string;
    startAt: string;
    trialEndAt: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    currency: string;
    billingCycle: string;
    provider: string;
}

export interface VerifyAuthResponse {
    userInfo: UserInfo;
    workspace: Workspace | null;
    workspacePlan: WorkspacePlan | null;
}

export interface NangoConnectTokenRequest {
    integration_id: string;
    [key: string]: any;
    axiosRequestConfig?: AxiosRequestConfig;
}

export interface NangoConnectTokenResponse {
    data?: { token?: string };
    result?: { token?: string };
    token?: string;
    action?: string;
    url?: string;
}

export interface NangoUnbindRequest {
    integration_id: string;
    axiosRequestConfig?: AxiosRequestConfig;
}

// ============ Workspace Integrations API Types ============

// Workspace Integration Mapping types (from API response)
export interface WorkspaceIntegrationMapping {
    last_synced_at: string | null;
    role?: string;
    updated_at: string;
    user_id: string;
    connection_id: string;
    account_name?: string | null;
    created_at: string;
    status?: string;
    customer_id?: string;
    time_zone?: string | null;
    currency_code?: string | null;
    account_id?: string;
    provider?: string;
    provider_config_key?: string;
    environment?: string;
    direct?: boolean;
    mapping_status?: string;
    account_status?: string;
}

// Workspace Integration type (from API response)
export interface WorkspaceIntegrationAPI {
    connected: boolean;
    environment: string;
    mappings: WorkspaceIntegrationMapping[];
    updated_at: string;
    provider: string;
    created_at: string;
    profile_data: any;
}

// Response format for workspace integrations API
export interface WorkspaceIntegrationsAPIResponse {
    [provider: string]: WorkspaceIntegrationAPI[];
}

export interface GetWorkspaceIntegrationsResponse {
    code: string;
    message: string;
    status: string;
    data: WorkspaceIntegrationsAPIResponse;
}

export class AuthService {
    private api: Api;

    constructor(config: ApiConfig) {
        this.api = new Api(config);
    }

    /**
     * 验证用户身份
     */
    public async verifyAuth(): Promise<VerifyAuthResponse> {
        try {
            const response = await this.api.get<VerifyAuthResponse>('/api/auth/verify');
            return response;
        } catch (error) {
            throw error;
        }
    }

    /**
     * 获取用户的 beta 状态
     */
    public async getBetaStatus(): Promise<BetaStatusResponse> {
        try {
            const response = await this.api.get<BetaStatusResponse>('/api/beta/status');
            return response;
        } catch (error) {
            return { betaAccess: true };
        }
    }

    /**
     * 兑换邀请码
     * @param code 邀请码
     */
    public async redeemInviteCode(code: string): Promise<RedeemCodeResponse> {
        try {
            const response = await this.api.post<RedeemCodeResponse>('/api/beta/redeem', { code });
            return { success: true, message: response.message };
        } catch (error) {
            return { success: false, message: 'Verify code failed' };
        }
    }

    /**
     * 绑定 Shopify 店铺
     * @param accessToken Shopify Admin API access token
     * @param domain Shopify 店铺域名
     */
    public async bindShop({ accessToken, domain }: { accessToken: string, domain: string }): Promise<string> {
        try {
            const response = await this.api.post<string>('/api/shop-binding/bind', { accessToken, domain });
            return response;
        } catch (error) {
            console.error('Bind shop failed:', error);
            return 'fail';
        }
    }

    /**
     * Connect to Shopify after Google login
     * @param shopDomain Shopify shop domain
     * @param redirectUrl Redirect URL after connection
     * @param authorization Authorization token
     */
    public async connectShopify(
        { shop_domain, redirect_url, axiosRequestConfig }: ShopifyConnectRequest
    ): Promise<ShopifyConnectResponse> {
        try {
            const response = await this.api.post<ShopifyConnectResponse>(
                '/api/shopify/connect',
                {
                    shop_domain: shop_domain,
                    redirect_url: redirect_url,
                    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Connect Shopify failed:', error);
            throw error;
        }
    }

    /**
     * Disconnect from Shopify store
     * @param data Shopify disconnect data
     * @returns Promise with disconnect result
     */
    public async disconnectShopify(
        { shop_domain, axiosRequestConfig }: ShopifyDisconnectRequest
    ): Promise<ShopifyDisconnectResponse> {
        try {
            const response = await this.api.post<ShopifyDisconnectResponse>(
                `/api/shopify/disconnect?shop_domain=${shop_domain}`,
                undefined,
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Disconnect Shopify failed:', error);
            throw error;
        }
    }

    /**
     * 获取 Google Ads 账户列表
     */
    public async getNangoGoogleAdsAccounts(): Promise<any> {
        try {
            const response = await this.api.get<any>('/api/nango/user/google-ads/accounts');
            return response;
        } catch (error) {
            console.error('Get Google Ads accounts failed:', error);
            throw error;
        }
    }

    /**
     * 获取 Nango 连接 Token
     */
    public async getNangoConnectToken(data: NangoConnectTokenRequest): Promise<NangoConnectTokenResponse> {
        try {
            const { axiosRequestConfig, ...requestData } = data;
            const response = await this.api.post<NangoConnectTokenResponse>(
                '/api/nango/connect-token',
                requestData,
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Get Nango connect token failed:', error);
            throw error;
        }
    }

    /**
     * 解绑 Nango 集成
     */
    public async unbindNangoIntegration(data: NangoUnbindRequest): Promise<void> {
        try {
            const { axiosRequestConfig, ...requestData } = data;
            await this.api.post('/api/nango/unbind', requestData, axiosRequestConfig);
        } catch (error) {
            console.error('Unbind Nango integration failed:', error);
            throw error;
        }
    }

    /**
     * 获取工作区下的所有集成
     * @param axiosRequestConfig - Optional axios config (must include X-Workspace-Id header)
     * @returns Promise with workspace integrations data
     */
    public async getWorkspaceIntegrations(
        axiosRequestConfig?: AxiosRequestConfig
    ): Promise<GetWorkspaceIntegrationsResponse> {
        try {
            const response = await this.api.get<GetWorkspaceIntegrationsResponse>(
                '/api/workspaces/integrations',
                axiosRequestConfig
            );
            return response;
        } catch (error) {
            console.error('Get workspace integrations failed:', error);
            throw error;
        }
    }

}

// 创建 AuthService 实例
export const authService = new AuthService({
    baseURL: config.services.auth.apiHost,
    headers: {'Content-Type': 'application/json'}
});